/**
 * 枚举与展示文案的**纯数据层**:`as const` 数组、派生类型、*_LABEL 映射、纯函数。
 *
 * 这里**不 import zod**。前端要的只是「stage 怎么翻成中文」这一半,而 `z.enum(...)`
 * 是模块初始化时执行的调用,tree-shaking 删不掉 —— 为了几个中文文案把整个 zod
 * 打进浏览器包(约 +40KB gz)不划算。
 *
 * **枚举仍然只有一个定义处**:值定义在这里,`enums.ts` 从这里派生 z.enum 与 schema。
 * 派生关系是代码不是约定,所以两边不可能漂。
 */

/** 七阶段。顺序即推进顺序,索引用于「已过阶段标绿」。终点是成交,不是签约推进。 */
export const CUSTOMER_STAGES = [
  "new_lead",
  "first_contact",
  "needs_confirmed",
  "solution_matched",
  "quote_negotiation",
  "closing",
  "won",
] as const;
export type CustomerStage = (typeof CUSTOMER_STAGES)[number];
/**
 * 阶段名按 2026-10-20 产品方案会定的销售漏斗:画像筛选出的线索 → 销售触达 → 需求挖掘 →
 * 方案PK → 商务谈判 → 成交。会上没有单列的「签约推进」保留(商务谈判谈完到合同落地之间)。
 * **只改了显示名,枚举值没动** —— 历史阶段记录、门禁、统计全按枚举值走。
 * ⚠️ 知识条目的「适用阶段」标签存的是显示名(knowledge_entries.fields),改名要带一条数据迁移(见 0034)。
 */
export const CUSTOMER_STAGE_LABEL: Record<CustomerStage, string> = {
  new_lead: "新线索",
  first_contact: "销售触达",
  needs_confirmed: "需求挖掘",
  solution_matched: "方案PK",
  quote_negotiation: "商务谈判",
  closing: "签约推进",
  won: "成交",
};
export const stageIndex = (s: CustomerStage): number => CUSTOMER_STAGES.indexOf(s);

/** 丢单不是阶段,是结算结果 —— 与 won 并列挂在 customer.outcome 上。 */
export const CUSTOMER_OUTCOMES = ["open", "won", "lost"] as const;
export type CustomerOutcome = (typeof CUSTOMER_OUTCOMES)[number];

/** 画像分级。D 在 PRD FR-5.1 出现过但 schema 只给 A/B/C,按三引擎规格取三档。 */
export const CUSTOMER_GRADES = ["A", "B", "C"] as const;
export type CustomerGrade = (typeof CUSTOMER_GRADES)[number];

/** 客户来源五途径。AI 分配是区别于传统 CRM 的差异化来源。 */
export const CUSTOMER_SOURCES = [
  "ai_assigned",
  "manager_assigned",
  "peer_handoff",
  "self_created",
  "bulk_import",
] as const;
export type CustomerSource = (typeof CUSTOMER_SOURCES)[number];
export const CUSTOMER_SOURCE_LABEL: Record<CustomerSource, string> = {
  ai_assigned: "🤖 AI分配",
  manager_assigned: "👤 主管指派",
  peer_handoff: "🔄 同事交接",
  self_created: "✏️ 自己新增",
  bulk_import: "📂 批量导入",
};

/** 生料五类投递通道。V2 的自动 feeder 喂进同一契约,只会往这里加值。 */
export const ARTIFACT_SOURCE_TYPES = [
  "call_recording",
  "chat_log",
  "onsite_recording",
  "meeting_record",
  "text_photo",
] as const;
export type ArtifactSourceType = (typeof ARTIFACT_SOURCE_TYPES)[number];
export const ARTIFACT_SOURCE_TYPE_LABEL: Record<ArtifactSourceType, string> = {
  call_recording: "通话录音",
  chat_log: "聊天记录",
  onsite_recording: "现场录音",
  meeting_record: "会议录音/录屏",
  text_photo: "文字/照片",
};

export const ARTIFACT_STATUSES = ["pending", "processing", "processed", "failed"] as const;
export type ArtifactStatus = (typeof ARTIFACT_STATUSES)[number];

/**
 * 摄取管线 6 步。第 0 步转码是文档没写但必须有的 ——
 * SenseVoice 限音频 ≤50MB,30 分钟 WAV 会超标。
 */
export const INGEST_STEPS = [
  "received",
  "transcoded",
  "transcribed",
  "extracted",
  "event_written",
  "decision_ran",
] as const;
export type IngestStep = (typeof INGEST_STEPS)[number];
export const INGEST_STEP_LABEL: Record<IngestStep, string> = {
  received: "已接收",
  transcoded: "转码",
  transcribed: "ASR 转写中",
  extracted: "结构化提取",
  event_written: "写入事件流",
  decision_ran: "更新动作卡",
};

/**
 * 摄取失败的归类。
 *
 * 53 屏里 `dataState` 定义了「错误」这一档却无一使用 —— 因为失败原因一直只有
 * 一句英文异常消息,界面上除了原样丢给销售看别无选择,而销售看到
 * `ECONNRESET` 只会去问运维。归类是为了回答两个问题:**这是谁的问题、能不能重试。**
 *
 * 列成 text 而不是 DB 枚举:失败类型会随着接的供应商增加而增加,
 * 而 `ALTER TYPE ADD VALUE` 不能在事务里回滚,加错一个值要靠重建类型才能撤。
 */
export const INGEST_FAILURE_KINDS = [
  "invalid_format",   // 生料本身有问题(空文本、非音频、编码乱码)
  "transcode_failed", // ffmpeg 转码失败
  "asr_failed",       // 转写服务报错或返回空
  "model_timeout",    // 模型超时 / 限流
  "extract_failed",   // 模型返回结构不合法,修复重试后仍不合法
  "storage_failed",   // 对象存储读写失败
  "quota_exceeded",   // 租户本月模型额度用完,调用没有发出去
  "internal",         // 兜底:代码 bug、数据库异常
] as const;
export type IngestFailureKind = (typeof INGEST_FAILURE_KINDS)[number];

export const INGEST_FAILURE_LABEL: Record<IngestFailureKind, string> = {
  invalid_format: "生料格式有问题",
  transcode_failed: "音频转码失败",
  asr_failed: "转写失败",
  model_timeout: "模型超时或限流",
  extract_failed: "结构化提取失败",
  storage_failed: "文件存储失败",
  quota_exceeded: "本月 AI 用量已达上限",
  internal: "系统内部错误",
};

/**
 * 能不能重试,以及重试之前要不要人先做点什么。
 * **格式错不可重试** —— 同一份坏文件重试一百次还是坏的,该让人重新传。
 */
export const INGEST_FAILURE_ADVICE: Record<IngestFailureKind, { retryable: boolean; advice: string }> = {
  invalid_format: { retryable: false, advice: "这份生料本身有问题,换一份重新投递" },
  transcode_failed: { retryable: false, advice: "音频可能已损坏或格式不受支持,换一份重新投递" },
  asr_failed: { retryable: true, advice: "转写服务临时不可用,可以重试" },
  model_timeout: { retryable: true, advice: "模型超时或被限流,稍后重试" },
  extract_failed: { retryable: true, advice: "模型这次输出不合规,重试通常就好" },
  storage_failed: { retryable: true, advice: "对象存储临时不可用,可以重试" },
  // 材料本身没问题,额度调高后重试就能接着处理 —— 所以可重试,但现在点了也没用,得先找服务方
  quota_exceeded: { retryable: true, advice: "本月 AI 用量已达上限,联系服务方调整额度后再重试" },
  internal: { retryable: true, advice: "系统内部错误,重试一次;仍失败请找运维看日志" },
};

/**
 * 事件的 5 类抽取信号 —— 进 event_signal 子表。
 * 另两个提取字段(AI 摘要、阶段信号)是单值,各占 event 表一列。
 */
export const EVENT_SIGNAL_KINDS = [
  "need",
  "objection",
  "buying_signal",
  "decision_maker",
  "competitor",
] as const;
export type EventSignalKind = (typeof EVENT_SIGNAL_KINDS)[number];
export const EVENT_SIGNAL_KIND_LABEL: Record<EventSignalKind, string> = {
  need: "需求",
  objection: "异议",
  buying_signal: "信号",
  decision_maker: "决策人",
  competitor: "竞品",
};

/** 知识库五分区。客户专属是唯一的客户级分区。 */
/**
 * 7 字段结构化提取(FR-12.2 的开关粒度)。
 *
 * key 与 `extractionSchema` 的字段名一一对应 —— tenant_settings.extraction_fields
 * 用的就是这些 key,两边对不上的后果是开关点了没反应,而且不报错。
 */
export const EXTRACTION_FIELDS = [
  "summary",
  "needs",
  "objections",
  "buyingSignals",
  "decisionMakers",
  "stageSignal",
  "competitors",
] as const;
export type ExtractionField = (typeof EXTRACTION_FIELDS)[number];
export const EXTRACTION_FIELD_LABEL: Record<ExtractionField, string> = {
  summary: "📝 AI 摘要",
  needs: "💡 客户需求",
  objections: "⚠️ 异议/顾虑",
  buyingSignals: "📊 购买/时机信号",
  decisionMakers: "👤 决策人线索",
  stageSignal: "🏷 阶段信号",
  competitors: "🏭 竞品信息",
};
export const EXTRACTION_FIELD_NOTE: Record<ExtractionField, string> = {
  summary: "整段沟通的核心要点概括",
  needs: "客户表达的业务问题和目标",
  objections: "价格、交付、竞品、信任等异议",
  buyingSignals: "预算已批、时间窗口、主动约见等",
  decisionMakers: "提到的人名、角色、联系方式",
  stageSignal: "判断商机应推进到哪个阶段",
  competitors: "客户提到的竞争对手名称和对比",
};

/**
 * 知识库分类。**2026-10-24 起由企业自己建**(`knowledge_categories`,迁移 0042;用户反馈:每家企业需要的类别不同)。
 * 代码里的 `partition` 就是分类的 key:企业新建的是 `c1`、`c2`…,下面五个是预置分类的 key ——
 * 新企业开通时自动建好,可以改名、删除,和企业自建的没有区别。
 * 分类只锁一个属性:可见性(`scope`),检索的可见范围与销售上传要不要审核都跟着它。
 */
export const KNOWLEDGE_PARTITIONS = [
  "company_standard",
  "case_and_script",
  "industry_and_competitor",
  "customer_specific",
  "method_and_sop",
] as const;
export type KnowledgePresetPartition = (typeof KNOWLEDGE_PARTITIONS)[number];
/** 分类 key:预置分类的 key,或企业新建的 `c<n>`。是不是这家企业有的,要查分类表。 */
export type KnowledgePartition = string;
export const KNOWLEDGE_CATEGORY_KEY_PATTERN = "^(c[0-9]{1,6}|company_standard|case_and_script|industry_and_competitor|customer_specific|method_and_sop)$";
/** 预置分类的初始名称与说明(迁移 0042 的种子用同一份;企业改过名以分类表为准)。 */
export const KNOWLEDGE_PARTITION_LABEL: Record<KnowledgePresetPartition, string> = {
  company_standard: "公司标准资料",
  case_and_script: "案例与话术",
  industry_and_competitor: "行业与竞对",
  customer_specific: "客户专属资料",
  method_and_sop: "方法论与 SOP",
};
export const KNOWLEDGE_PARTITION_DESCRIPTION: Record<KnowledgePresetPartition, string> = {
  company_standard: "公司与产品介绍、通用解决方案、技术资料、标准报价或合同模板、资质。",
  case_and_script: "可复用的成功案例、ROI 案例、销售话术、异议处理、谈判技巧。",
  industry_and_competitor: "行业研究、市场洞察、竞品介绍和比较。",
  customer_specific: "针对某个实际客户的定制方案、具体报价、合同、会议或沟通纪要。通用空白模板不属于此类。",
  method_and_sop: "销售流程、阶段指引、SOP、新人培训和方法论。",
};

export const KNOWLEDGE_SCOPES = ["tenant", "customer"] as const;
export type KnowledgeScope = (typeof KNOWLEDGE_SCOPES)[number];
export const KNOWLEDGE_SCOPE_LABEL: Record<KnowledgeScope, string> = {
  tenant: "团队共享",
  customer: "按客户权限",
};
/** 预置分类的可见性。企业的分类以分类表的 `scope` 为准,上传者选不了 scope。 */
export const PARTITION_SCOPE: Record<KnowledgePresetPartition, KnowledgeScope> = {
  company_standard: "tenant",
  case_and_script: "tenant",
  industry_and_competitor: "tenant",
  customer_specific: "customer",
  method_and_sop: "tenant",
};

export const KNOWLEDGE_ENTRY_TYPES = {
  product: ["company_standard", "产品介绍"], solution: ["company_standard", "通用解决方案"], technical: ["company_standard", "技术资料"], template: ["company_standard", "报价与合同模板"], qualification: ["company_standard", "公司资质"],
  case: ["case_and_script", "成功案例"], script: ["case_and_script", "阶段话术"], objection: ["case_and_script", "异议处理"], negotiation: ["case_and_script", "谈判经验"],
  industry: ["industry_and_competitor", "行业资料"], competitor: ["industry_and_competitor", "竞品档案"], comparison: ["industry_and_competitor", "竞品对比"],
  customer_need: ["customer_specific", "客户需求"], customer_solution: ["customer_specific", "定制方案"], customer_quote: ["customer_specific", "具体报价"], customer_contract: ["customer_specific", "客户合同"], meeting: ["customer_specific", "沟通纪要"], customer_reference: ["customer_specific", "客户参考资料"],
  sop: ["method_and_sop", "流程 SOP"], stage_guide: ["method_and_sop", "阶段指引"], training: ["method_and_sop", "培训指南"],
} as const satisfies Record<string, readonly [KnowledgePresetPartition, string]>;
export type KnowledgeEntryKind = keyof typeof KNOWLEDGE_ENTRY_TYPES;
/**
 * 企业在分类下自加的知识类型(2026-10-24 用户反馈:知识库分类放进系统设置,可自定义)。
 * key 系统生成(k1、k2…)且不变,和内置类型一样存进 `knowledge_entries.kind`。
 * 内置类型挂在预置分类下,预置分类被删了,它下面的内置类型也就不再可选。
 */
export type CustomKnowledgeEntryKind = `k${number}`;
export type KnowledgeKind = KnowledgeEntryKind | CustomKnowledgeEntryKind;
export const isCustomKnowledgeKind = (kind: string): kind is CustomKnowledgeEntryKind => /^k[0-9]+$/.test(kind);
export const KNOWLEDGE_FIELD_LABEL = { product: "产品", scenario: "场景", industry: "行业", pain: "痛点 / 异议", stage: "适用阶段", competitor: "竞争对手", dimension: "对比维度", role: "适用角色", task: "任务", date: "资料日期" } as const;
export type KnowledgeFieldKey = keyof typeof KNOWLEDGE_FIELD_LABEL;
export interface KnowledgeEntryField { key: KnowledgeFieldKey; value: string; quote: string }
export const KNOWLEDGE_QUERY_FIELDS: Record<KnowledgePresetPartition, readonly KnowledgeFieldKey[]> = {
  company_standard: ["product", "scenario"], case_and_script: ["industry", "pain", "stage"], industry_and_competitor: ["industry", "competitor", "dimension"], customer_specific: ["stage", "date", "product"], method_and_sop: ["stage", "role", "task"],
};
/** 企业自建分类的筛选字段。 */
export const DEFAULT_KNOWLEDGE_QUERY_FIELDS: readonly KnowledgeFieldKey[] = ["product", "industry", "stage"];
export const knowledgeQueryFields = (partition: string): readonly KnowledgeFieldKey[] =>
  (KNOWLEDGE_QUERY_FIELDS as Record<string, readonly KnowledgeFieldKey[]>)[partition] ?? DEFAULT_KNOWLEDGE_QUERY_FIELDS;
/**
 * 系统自动产生的资料放进哪个分类(`knowledge_category_routes`)。原来写死在代码里,分类放开之后由企业指定;
 * 被指定的分类删除前要先改去向(外键挡着)。`scope` 是这条去向要求的可见性 —— 客户沟通摘要只能进按客户权限的分类。
 */
export const KNOWLEDGE_CATEGORY_ROUTES = ["retro", "retro_competitor", "objection", "customer"] as const;
export type KnowledgeCategoryRoute = (typeof KNOWLEDGE_CATEGORY_ROUTES)[number];
export const KNOWLEDGE_CATEGORY_ROUTE_LABEL: Record<KnowledgeCategoryRoute, string> = {
  retro: "赢单 / 丢单复盘", retro_competitor: "输给竞品的丢单复盘", objection: "异议沉淀", customer: "客户沟通摘要、交接纪要与指定客户的上传",
};
export const KNOWLEDGE_CATEGORY_ROUTE_SCOPE: Record<KnowledgeCategoryRoute, KnowledgeScope> = {
  retro: "tenant", retro_competitor: "tenant", objection: "tenant", customer: "customer",
};
export const KNOWLEDGE_PROCESSING_LABEL = { pending: "待整理", processing: "整理中", ready: "已整理", failed: "整理失败", needs_customer: "待指定客户", paused: "已暂停" } as const;
export type KnowledgeProcessingState = keyof typeof KNOWLEDGE_PROCESSING_LABEL;
/**
 * 资料是否参与检索。销售上传到团队分区的资料要主管审核后才生效 ——
 * 一份写错的报价进了团队区,会马上变成全队动作卡的出处。其余来源一律直接生效。
 */
export const KNOWLEDGE_REVIEW_LABEL = { approved: "已生效", pending: "待审核" } as const;
export type KnowledgeReviewState = keyof typeof KNOWLEDGE_REVIEW_LABEL;

export const ACTION_CARD_STATUSES = ["pending", "adopted", "ignored", "done", "expired"] as const;
export type ActionCardStatus = (typeof ACTION_CARD_STATUSES)[number];

/** 画像版本的成因。原型 E01「版本历史」:v1 冷启动 → v2 成交回流 → v3 沟通回填。 */
export const PROFILE_VERSION_CAUSES = ["cold_start", "deal_feedback", "event_backfill", "manual"] as const;
export type ProfileVersionCause = (typeof PROFILE_VERSION_CAUSES)[number];

/** 画像维度的来源。「沟通回填」是让维度命中可在导入后改变、从而让当前分动态升降的授权位。 */
export const PROFILE_DIM_SOURCES = ["inferred", "inferred_plus_backfill", "manual"] as const;
export type ProfileDimSource = (typeof PROFILE_DIM_SOURCES)[number];

export const USER_ROLES = ["sales", "manager", "admin"] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** 自主度档位。值与文案是纯数据;schema 与动作映射表在 autonomy.ts。 */
export const AUTONOMY_LEVELS = [0, 1, 2, 3, 4] as const;
export const AUTONOMY_LEVEL_LABEL: Record<(typeof AUTONOMY_LEVELS)[number], string> = {
  0: "只观察",
  1: "只建议",
  2: "待批执行(一键确认)",
  3: "先斩后奏(可撤销)",
  4: "全自主",
};

// ── 企业自定义客户字段 ───────────────────────────────────────────

/**
 * 标准客户信息表之外,企业自己追加的字段(2026-10-20 产品方案会 40:04:每家企业的
 * 客户信息表字段都不一样,复杂的一个客户要几十个字段)。值按类型存:
 * number 存数字、date 存 `YYYY-MM-DD`、select 存选项原文、text 存原文。
 * 画像规则用 `cf.<key>` 引用它们,key 由系统生成且不变,label 才是企业起的名字。
 */
export const CUSTOMER_FIELD_TYPES = ["text", "number", "select", "date"] as const;
export type CustomerFieldType = (typeof CUSTOMER_FIELD_TYPES)[number];
export const CUSTOMER_FIELD_TYPE_LABEL: Record<CustomerFieldType, string> = {
  text: "文本", number: "数字", select: "单选", date: "日期",
};
/** 画像规则里引用自定义字段的前缀:`cf.f3 >= 1000`。 */
export const CUSTOM_FIELD_RULE_PREFIX = "cf.";
/**
 * 标准客户信息表的字段(customers 表上的列,每家企业都有),自定义字段追加在它们之后。
 * 界面上「客户字段」分区把它们列成只读的「系统」行;导入映射、详情编辑的标签也从这里取。
 */
export const CUSTOMER_STANDARD_FIELDS = ["name", "legalName", "industry", "region", "sizeBand", "address", "registeredCapital", "products"] as const;
export type CustomerStandardField = (typeof CUSTOMER_STANDARD_FIELDS)[number];
export const CUSTOMER_STANDARD_FIELD_LABEL: Record<CustomerStandardField, string> = {
  name: "企业名称", legalName: "法定代表人", industry: "所属行业", region: "所属地区",
  sizeBand: "人员规模", address: "注册地址", registeredCapital: "注册资本", products: "主营产品",
};

// ── 组织与权限 ─────────────────────────────────────────────────

/**
 * 三档角色是**工作台分层**:决定一个人进销售工作台还是管理工作台、能碰哪一类接口。
 * 自定义角色必须挂在其中一档上,而且**只能在这一档允许的权限点里勾选** ——
 * 自定义角色只能比基准收得更紧,不能越档。这样「加一个角色」最坏的结果是
 * 某人少了点功能(立刻有人来问),而不是某个销售悄悄拿到了管理端权限(没人发现)。
 */
export const ROLE_TIERS = USER_ROLES;
export const ROLE_TIER_LABEL: Record<UserRole, string> = { sales: "销售", manager: "管理者", admin: "管理员" };

export const PERMISSION_GROUPS = [
  { key: "customer", label: "客户", permissions: [
    { key: "customer.view", label: "查看客户", tier: "sales" },
    { key: "customer.create", label: "新增客户", tier: "sales" },
    { key: "customer.edit", label: "编辑客户与录入沟通", tier: "sales" },
    { key: "customer.claim", label: "领取公海客户", tier: "sales" },
    { key: "customer.import", label: "导入名单", tier: "manager" },
    { key: "customer.assign", label: "分配与改派客户", tier: "manager" },
    { key: "customer.fields", label: "自定义客户字段", tier: "manager" },
  ] },
  { key: "deal", label: "阶段与成交", permissions: [
    { key: "stage.correct", label: "修正阶段", tier: "sales" },
    { key: "deal.settle", label: "标记成交 / 丢单", tier: "sales" },
  ] },
  { key: "knowledge", label: "知识库", permissions: [
    { key: "knowledge.view", label: "查看与检索资料", tier: "sales" },
    { key: "knowledge.upload", label: "上传资料", tier: "sales" },
    { key: "knowledge.manage", label: "审核、分类与删除资料", tier: "manager" },
  ] },
  { key: "engine", label: "三引擎配置", permissions: [
    { key: "engine.profile", label: "画像引擎", tier: "manager" },
    { key: "engine.ingest", label: "录入引擎与沟通分析", tier: "manager" },
    { key: "engine.knowledge", label: "知识引擎配置", tier: "manager" },
  ] },
  { key: "org", label: "组织与权限", permissions: [
    { key: "org.department", label: "部门管理", tier: "manager" },
    { key: "org.member", label: "成员管理与 AI 权限", tier: "manager" },
    { key: "org.role", label: "角色与权限管理", tier: "admin" },
  ] },
  { key: "data", label: "数据与统计", permissions: [
    { key: "stats.view", label: "经营统计、漏斗与风险", tier: "manager" },
    { key: "data.export", label: "导出企业全部数据", tier: "admin" },
  ] },
] as const;

export type PermissionKey = (typeof PERMISSION_GROUPS)[number]["permissions"][number]["key"];
export const PERMISSIONS: readonly { key: PermissionKey; label: string; tier: UserRole }[] = PERMISSION_GROUPS.flatMap((g): readonly { key: PermissionKey; label: string; tier: UserRole }[] => g.permissions);
export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);
export const PERMISSION_LABEL = Object.fromEntries(PERMISSIONS.map((p) => [p.key, p.label])) as Record<PermissionKey, string>;

const TIER_RANK: Record<UserRole, number> = { sales: 0, manager: 1, admin: 2 };
/** 这一档最多能拥有的权限点。 */
export function tierPermissions(tier: UserRole): PermissionKey[] {
  return PERMISSIONS.filter((p) => TIER_RANK[p.tier] <= TIER_RANK[tier]).map((p) => p.key);
}

/**
 * 客户数据可见范围。**只管「看得见哪些客户」,不管能做什么操作。**
 * 「本部门」「及下级」按部门树算;公海(没有负责人)的客户对管理者始终可见 ——
 * 否则只看本部门的主管连待分配的名单都看不到,也就没法分给自己的组员。
 */
export const DATA_SCOPES = ["self", "department", "department_tree", "all"] as const;
export type DataScope = (typeof DATA_SCOPES)[number];
export const DATA_SCOPE_LABEL: Record<DataScope, string> = {
  self: "仅本人", department: "本部门", department_tree: "本部门及下级", all: "全部",
};
/**
 * 每档能选的范围。销售端的界面与查询都按「自己负责的客户」写死,
 * 放开会让「我的客户」混进同事的客户,所以销售档只有「仅本人」。
 */
export const TIER_DATA_SCOPES: Record<UserRole, readonly DataScope[]> = {
  sales: ["self"], manager: ["self", "department", "department_tree", "all"], admin: ["all"],
};
export const DATA_SCOPE_RANK: Record<DataScope, number> = { self: 0, department: 1, department_tree: 2, all: 3 };

export interface BuiltinRole {
  readonly key: UserRole; readonly name: string; readonly tier: UserRole;
  readonly permissions: readonly PermissionKey[]; readonly dataScope: DataScope;
  readonly aiCap: (typeof AUTONOMY_LEVELS)[number]; readonly description: string;
}
/**
 * 系统预置角色,**与本次改动之前三种角色的行为完全一致**:
 * 管理者能做除「角色与权限管理」以外的一切、看全部客户;销售只看自己的客户。
 * 预置角色不可改不可删,要调就复制成自定义角色再改。
 */
export const BUILTIN_ROLES: Record<UserRole, BuiltinRole> = {
  admin: { key: "admin", name: "管理员", tier: "admin", permissions: tierPermissions("admin"), dataScope: "all", aiCap: 4, description: "全部权限,含角色与权限管理" },
  manager: { key: "manager", name: "管理者", tier: "manager", permissions: tierPermissions("manager"), dataScope: "all", aiCap: 4, description: "客户管理、三引擎配置与成员管理,看全部客户" },
  sales: { key: "sales", name: "销售", tier: "sales", permissions: tierPermissions("sales"), dataScope: "self", aiCap: 4, description: "只跟进自己名下客户" },
};

/**
 * 《用户协议》《隐私政策》的版本(= 生效日期)。登录时前端带上这个值,后端记到 users 上 ——
 * 出了纠纷能答得出「这个人在哪天同意的是哪一版」。**改了条款就改这个日期**,
 * 正文在 apps/web/src/legal.tsx。
 */
export const LEGAL_VERSION = "2026-10-20";

/**
 * 报表中心(原型 G01–G06)。周期是「完整的上一期」:周一生成的周报统计上周一到周日,
 * 不统计本周刚开始的一天 —— 拿一天的数和整周比环比,永远是「大幅下滑」。
 */
export const REPORT_PERIODS = ["week", "month", "quarter"] as const;
export type ReportPeriod = (typeof REPORT_PERIODS)[number];
export const REPORT_PERIOD_LABEL: Record<ReportPeriod, string> = { week: "周", month: "月", quarter: "季度" };

/** 报表可选的指标。key 是存进模板的值,别改;文案可以改。 */
export const REPORT_METRIC_GROUPS = [
  { key: "deal", label: "业绩", metrics: [
    { key: "won_amount", label: "签约金额" },
    { key: "won_count", label: "成交数" },
    { key: "avg_deal", label: "客单价" },
    { key: "win_rate", label: "成交率" },
    { key: "avg_cycle", label: "平均成交周期" },
  ] },
  { key: "customer", label: "客户", metrics: [
    { key: "new_customers", label: "新增客户" },
    { key: "active_customers", label: "有沟通的客户" },
    { key: "lost_count", label: "丢单数" },
  ] },
  { key: "process", label: "过程", metrics: [
    { key: "communications", label: "沟通次数" },
    { key: "cards_generated", label: "AI 建议数" },
    { key: "adoption_rate", label: "建议采纳率" },
  ] },
  { key: "chart", label: "图表", metrics: [
    { key: "trend", label: "近 8 期签约趋势" },
    { key: "funnel", label: "漏斗转化" },
    { key: "ranking", label: "团队排行" },
    { key: "loss_reasons", label: "丢单原因" },
  ] },
] as const;
export type ReportMetric = (typeof REPORT_METRIC_GROUPS)[number]["metrics"][number]["key"];
export const REPORT_METRICS: readonly ReportMetric[] = REPORT_METRIC_GROUPS.flatMap((g) => g.metrics.map((m) => m.key));
export const REPORT_METRIC_LABEL = Object.fromEntries(REPORT_METRIC_GROUPS.flatMap((g) => g.metrics.map((m) => [m.key, m.label]))) as Record<ReportMetric, string>;

/**
 * 知识缺口(2026-10-20 产品方案会 13:35:知识库就是培训 AI 和新人的全部资料,库里没有,新人也一样不会)。
 * 助手答不上、动作卡因检索不到知识没出卡时各记一条,相同的问题合并计数,给有知识库管理权限的人补资料。
 * 「检索不到就直说没有」不变 —— 缺口只是让「答不上」变成可以补的东西。
 */
export const KNOWLEDGE_GAP_SOURCES = ["assistant", "action_card"] as const;
export type KnowledgeGapSource = (typeof KNOWLEDGE_GAP_SOURCES)[number];
export const KNOWLEDGE_GAP_SOURCE_LABEL: Record<KnowledgeGapSource, string> = { assistant: "AI 助手", action_card: "动作卡" };
export const KNOWLEDGE_GAP_STATUSES = ["open", "filled", "ignored"] as const;
export type KnowledgeGapStatus = (typeof KNOWLEDGE_GAP_STATUSES)[number];
export const KNOWLEDGE_GAP_STATUS_LABEL: Record<KnowledgeGapStatus, string> = { open: "待补充", filled: "已补充", ignored: "已忽略" };

/** 企业首次使用的准备步骤；进度与客户数量无关。 */
export const ENTERPRISE_SETUP_STEPS = ["fields", "profile", "knowledge", "team"] as const;
export type EnterpriseSetupStep = (typeof ENTERPRISE_SETUP_STEPS)[number];
export const ENTERPRISE_SETUP_LABEL: Record<EnterpriseSetupStep,string> = { fields:"客户信息表",profile:"目标客户画像",knowledge:"知识库分类",team:"团队与权限" };
